import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Container, Row, Col, Table, Spinner, Alert } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../config/properties';
import authService from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import './LottoExtractionHistory.css';

/**
 * Component to display Lotto extraction history with optional date range and wheel filtering
 */
const LottoExtractionHistory = ({ selectedWheel }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState(50);
  const [wheels, setWheels] = useState([]);
  const [wheelFilter, setWheelFilter] = useState('');

  // Load available wheels only if authenticated
  useEffect(() => {
    const loadWheels = async () => {
      // Don't load wheels if not authenticated
      if (!isAuthenticated) {
        console.debug('Not loading wheels - user not authenticated');
        return;
      }

      try {
        const headers = authService.getAuthHeader();
        const token = authService.getToken();

        console.debug('Loading wheels - Token present:', !!token);
        console.debug('Auth header:', headers);

        const response = await fetch(API_ENDPOINTS.lottoWheels, {
          method: 'GET',
          headers: headers,
          credentials: 'include', // Include credentials for CORS
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`HTTP error! status: ${response.status}, body: ${errorText}`);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setWheels(Array.isArray(data) ? data : []);

        // Set initial wheel filter from selectedWheel if available
        if (selectedWheel) {
          const wheelCode = selectedWheel.code || selectedWheel;
          setWheelFilter(wheelCode);
        }
      } catch (err) {
        console.error('Error loading wheels:', err);
        setError(t('errorLoadingWheels'));
      }
    };

    loadWheels();
  }, [selectedWheel, t, isAuthenticated]);

  // Fetch extractions with optional wheel filter and date range
  const fetchExtractionsByDateRange = async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      setError(t('mustBeLoggedIn') || 'You must be logged in to perform this action');
      return;
    }

    // Validate date range only if both dates are provided
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      setError(t('invalidDateRange'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build URL with optional parameters
      const params = new URLSearchParams();

      if (wheelFilter) {
        params.append('wheelCode', wheelFilter);
      }

      if (fromDate) {
        params.append('from', fromDate);
      }

      if (toDate) {
        params.append('to', toDate);
      }

      params.append('limit', limit);

      const url = `${API_ENDPOINTS.lottoExtractions}?${params.toString()}`;
      const headers = authService.getAuthHeader();
      const token = authService.getToken();

      console.debug('Fetching extractions - URL:', url);
      console.debug('Token present:', !!token);
      console.debug('Auth header:', headers);
      console.debug('Request params:', { wheelFilter, fromDate, toDate, limit });

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include', // Include credentials for CORS
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP error! status: ${response.status}, body: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.debug('Extractions fetched successfully:', data?.length || 0, 'records');
      setExtractions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching extractions:', err);
      setError(t('errorFetchingData') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle reset
  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setWheelFilter('');
    setExtractions([]);
    setError(null);
  };

  // Format numbers for display
  const formatNumbers = (extraction) => {
    if (!extraction.numbers || extraction.numbers.length === 0) {
      return '-';
    }
    return extraction.numbers.join(', ');
  };

  return (
    <Container fluid className="lotto-extraction-history py-4">
      <Row className="mb-4">
        <Col md={12}>
          <h5 className="text-primary mb-3">
            <i className="bi bi-calendar-event"></i> {t('extractionHistory')}
          </h5>

          {/* Search Form */}
          <div className="card card-search mb-4">
            <div className="card-body">
              <Form>
                <Row className="mb-3">
                  <Col md={2} sm={6} xs={12} className="mb-2">
                    <Form.Group>
                      <Form.Label className="fw-bold">{t('wheel')}</Form.Label>
                      <Form.Select
                        value={wheelFilter}
                        onChange={(e) => setWheelFilter(e.target.value)}
                      >
                        <option value="">{t('allWheels')}</option>
                        {wheels.map((wheel) => (
                          <option key={wheel.code} value={wheel.code}>
                            {wheel.name} ({wheel.code})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={2} sm={6} xs={12} className="mb-2">
                    <Form.Group>
                      <Form.Label className="fw-bold">{t('fromDate')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2} sm={6} xs={12} className="mb-2">
                    <Form.Group>
                      <Form.Label className="fw-bold">{t('toDate')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={1} sm={6} xs={12} className="mb-2">
                    <Form.Group>
                      <Form.Label className="fw-bold">{t('limit')}</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="1000"
                        value={limit}
                        onChange={(e) => setLimit(parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={5} sm={6} xs={12} className="mb-2">
                    <Form.Group>
                      <Form.Label>&nbsp;</Form.Label>
                      <div className="d-flex gap-2">
                        <Button
                          variant="primary"
                          onClick={fetchExtractionsByDateRange}
                          disabled={loading}
                          className="flex-grow-1"
                        >
                          {loading ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              {t('searching')}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-search"></i> {t('search')}
                            </>
                          )}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          <i className="bi bi-arrow-counterclockwise"></i> {t('reset')}
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              <i className="bi bi-exclamation-triangle"></i> {error}
            </Alert>
          )}

          {/* Results Table */}
          {extractions.length > 0 ? (
            <div className="table-responsive">
              <Table striped bordered hover className="extraction-table">
                <thead className="table-header">
                  <tr>
                    <th className="text-center">ID</th>
                    <th className="text-center">{t('wheel')}</th>
                    <th className="text-center">{t('extractionDate')}</th>
                    <th className="text-center">{t('numbers')} (5)</th>
                  </tr>
                </thead>
                <tbody>
                  {extractions.map((extraction, index) => (
                    <tr key={extraction.id || index}>
                      <td className="text-center text-muted">{extraction.id}</td>
                      <td className="text-center fw-bold">{extraction.wheelName} ({extraction.wheelCode})</td>
                      <td className="text-center">{extraction.extractionDate}</td>
                      <td className="text-center">
                        <span className="badge badge-numbers">{formatNumbers(extraction)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : !loading && extractions.length === 0 && !error && (fromDate || toDate || wheelFilter) ? (
            <Alert variant="info">
              <i className="bi bi-info-circle"></i> {t('noResourcesFound')}
            </Alert>
          ) : null}

          {/* Loading Indicator */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">{t('loading')}</span>
              </Spinner>
              <p className="mt-3 text-muted">{t('loadingData')}</p>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default LottoExtractionHistory;
